import { useQueryClient } from '@tanstack/react-query';
import { KeyedError, getErrorsFromPromises } from '@zorro/common-utils';
import _chunk from 'lodash/chunk';
import { useState } from 'react';

import {
  MonolithMethodNames,
  MonolithMethods,
  callEndpoint,
} from '../callEndpoint';
import { handleBatchNotifications } from '../handleBatchNotifications';
import { useDynamicRouter } from './useDynamicRouter';
import { useLoadingOverlay } from './useLoadingOverlay';

export type BatchItem<M extends MonolithMethodNames = MonolithMethodNames> = {
  key: string;
  params: readonly [...Parameters<MonolithMethods[M]>];
  /**
   * Optional grouping key. Items sharing a `batchKey` are placed in separate
   * batches so they never run concurrently (batches run sequentially, items
   * within a batch run in parallel). Use it when concurrent calls that touch a
   * shared backend row would conflict - e.g. onboarding periods of the same
   * employee. Items without a `batchKey` fall back to plain fixed-size batches.
   */
  batchKey?: string;
};

/**
 * Split items into sequential batches. Each item goes into the earliest
 * non-full batch that does not already contain its `batchKey`, so two items
 * sharing a key always end up in different (sequentially executed) batches
 * while distinct keys still fill a batch up to `batchSize`. When no item has a
 * `batchKey` (the common case) this is just plain fixed-size chunking.
 */
function buildBatches<M extends MonolithMethodNames>(
  items: BatchItem<M>[],
  batchSize: number
): BatchItem<M>[][] {
  if (items.every((item) => !item.batchKey)) {
    return _chunk(items, batchSize);
  }

  const batches: { items: BatchItem<M>[]; keys: Set<string> }[] = [];

  for (const item of items) {
    const { batchKey } = item;
    const target = batches.find(
      (batch) =>
        batch.items.length < batchSize &&
        (!batchKey || !batch.keys.has(batchKey))
    );

    if (target) {
      target.items.push(item);
      if (batchKey) target.keys.add(batchKey);
    } else {
      batches.push({
        items: [item],
        keys: batchKey ? new Set([batchKey]) : new Set(),
      });
    }
  }

  return batches.map((batch) => batch.items);
}

type UseBatchCallEndpointOptions<M extends MonolithMethodNames> = {
  methodName: M;
  singularItemName: string;
  action: string;
  batchSize?: number;
};

type ExecuteBatchCallOptions = {
  onSuccess?: () => void;
  onError?: () => void;
  shouldAvoidPageReload?: boolean;
  shouldShowNotifications?: boolean;
};

export const useBatchCallEndpoint = <
  M extends MonolithMethodNames = MonolithMethodNames,
>({
  methodName,
  singularItemName,
  action,
  batchSize = 5,
}: UseBatchCallEndpointOptions<M>) => {
  const queryClient = useQueryClient();
  const { reloadPage } = useDynamicRouter({});
  const { startLoading, stopLoading } = useLoadingOverlay();

  const [errors, setErrors] = useState<KeyedError[]>([]);

  type MethodReturnType = Awaited<ReturnType<MonolithMethods[M]>>;

  const executeBatchCall = async (
    batchItems: BatchItem<M>[],
    options?: ExecuteBatchCallOptions
  ): Promise<{
    data: PromiseSettledResult<MethodReturnType>[];
    errors: KeyedError[];
  }> => {
    try {
      startLoading();
      setErrors([]);

      const batches = buildBatches(batchItems, batchSize);

      const results: PromiseSettledResult<MethodReturnType>[] = [];

      for (const batch of batches) {
        const batchResults = await Promise.allSettled(
          batch.map((item) => {
            return callEndpoint<M>({
              method: methodName,
              params: item.params as Parameters<MonolithMethods[M]>,
            });
          })
        );

        results.push(...batchResults);
      }

      setTimeout(async () => {
        await queryClient.invalidateQueries();
      }, 0);

      if (!options?.shouldAvoidPageReload) reloadPage();

      stopLoading();

      if (options?.shouldShowNotifications ?? true) {
        await handleBatchNotifications(
          results,
          { singular: singularItemName },
          action
        );
      }

      const errorsFromPromises = getErrorsFromPromises(results, batchItems);

      setErrors(errorsFromPromises);

      if (errorsFromPromises.length === 0) {
        options?.onSuccess?.();
      } else {
        options?.onError?.();
      }

      return { data: results, errors: errorsFromPromises };
    } catch (error) {
      stopLoading();
      throw error;
    }
  };

  return {
    executeBatchCall,
    errors,
  };
};
